import React, { useState, useEffect } from 'react';

const PointsUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('balance');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({
    partnerId: '',
    status: '',
    minBalance: '',
    maxBalance: ''
  });

  // Fetch users when page, filters or sort changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, itemsPerPage, sortField, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare query parameters
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        sort: sortField,
        order: sortOrder
      });

      // Add filters if they exist
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/points/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch points users');
      }

      const data = await response.json();
      setUsers(data.data);
      setTotalRecords(data.pagination.total);
    } catch (err) {
      console.error('Error fetching points users:', err);
      setError('Failed to load points users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for a new field
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchUsers();
  };

  const handleClearFilters = () => {
    setFilters({
      partnerId: '',
      status: '',
      minBalance: '',
      maxBalance: ''
    });
    setCurrentPage(1);
    setSortField('balance');
    setSortOrder('desc');
    fetchUsers();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Get status badge style based on status value
  const getStatusBadgeStyle = (status) => {
    let color;
    switch (status) {
      case 'active':
        color = '#4CAF50'; // Green
        break;
      case 'suspended':
        color = '#FF9800'; // Orange
        break;
      case 'closed':
        color = '#F44336'; // Red
        break;
      default:
        color = '#9E9E9E'; // Gray
    }

    return {
      backgroundColor: color,
      color: 'white',
      borderRadius: '12px',
      padding: '3px 8px',
      fontSize: '12px',
      display: 'inline-block'
    };
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Generate page numbers
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div>
      <h3>Points Users</h3>
      
      {/* Filters */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
        <form onSubmit={handleApplyFilters}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Partner:</label>
              <input
                type="text"
                name="partnerId"
                value={filters.partnerId}
                onChange={handleFilterChange}
                placeholder="Partner ID"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Status:</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Min Balance:</label>
              <input
                type="number"
                name="minBalance"
                value={filters.minBalance}
                onChange={handleFilterChange}
                placeholder="Min"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px' }}>Max Balance:</label>
              <input
                type="number"
                name="maxBalance"
                value={filters.maxBalance}
                onChange={handleFilterChange}
                placeholder="Max"
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="submit"
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                Apply Filters
              </button>
              
              <button
                type="button"
                onClick={handleClearFilters}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Users Table */}
      {loading ? (
        <div>Loading users...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('_id')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      ID
                      {sortField === '_id' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('partnerId')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Partner
                      {sortField === 'partnerId' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('balance')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Balance
                      {sortField === 'balance' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('status')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Status
                      {sortField === 'status' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>Total Earned</th>
                  <th style={tableHeaderStyle}>Total Spent</th>
                  <th style={tableHeaderStyle}>
                    <div 
                      onClick={() => handleSort('lastTransaction')} 
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      Last Transaction
                      {sortField === 'lastTransaction' && (
                        <span style={{ marginLeft: '5px' }}>
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th style={tableHeaderStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px 0' }}>
                      No points users found
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={tableCellStyle}>{user._id.substring(0, 8)}...</td>
                      <td style={tableCellStyle}>
                        {user.partnerId?.name || user.partnerId || 'Unknown'}
                      </td>
                      <td style={tableCellStyle}>{user.balance.toLocaleString()}</td>
                      <td style={tableCellStyle}>
                        <span style={getStatusBadgeStyle(user.status)}>
                          {user.status}
                        </span>
                      </td>
                      <td style={tableCellStyle}>{user.totalEarned?.toLocaleString() || 0}</td>
                      <td style={tableCellStyle}>{user.totalSpent?.toLocaleString() || 0}</td>
                      <td style={tableCellStyle}>{formatDate(user.lastTransaction)}</td>
                      <td style={tableCellStyle}>
                        <button
                          onClick={() => {/* View details function */}}
                          style={{
                            backgroundColor: '#4f46e5',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 10px',
                            marginRight: '5px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => {/* Edit function */}}
                          style={{
                            backgroundColor: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', alignItems: 'center' }}>
            <div>
              Showing {users.length} of {totalRecords} entries
            </div>
            <div>
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '5px 10px',
                  marginRight: '5px',
                  backgroundColor: currentPage === 1 ? '#e5e7eb' : '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              
              {pageNumbers.map(number => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  style={{
                    padding: '5px 10px',
                    marginRight: '5px',
                    backgroundColor: currentPage === number ? '#4f46e5' : '#f3f4f6',
                    color: currentPage === number ? 'white' : 'black',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {number}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '5px 10px',
                  backgroundColor: currentPage === totalPages ? '#e5e7eb' : '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Styles
const tableHeaderStyle = {
  padding: '12px 15px',
  textAlign: 'left',
  borderBottom: '2px solid #d1d5db',
  fontSize: '14px'
};

const tableCellStyle = {
  padding: '12px 15px',
  fontSize: '14px'
};

export default PointsUsersTab;